import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLoaderData, useNavigate, useRevalidator } from 'react-router';
import { paths } from '../../common/constants/pathVariables.js';
import { CommonErrorType, DialogType } from '../../common/enums.js';
import { fetchBillingAccounts } from '../../common/fetch.js';
import { useSyncCartPricesWithModels } from '../../common/hooks/useSyncCartPricesWithModels';
import { corporateMessagePopUpHeaderMsg, t } from '../../common/i18n/index.js';
import { getConfiguredCommercialProducts } from '../../common/utils/cartProductUtils.js';
import { getPublishedCatalogs } from '../../common/utils/catalogUtils.js';
import { getDeliveryCharges, getDeliveryDetailsObj, hasEmployeePayables } from '../../common/utils/checkoutUtils.js';
import { deepEqual } from '../../common/utils/objectUtils.js';
import { findPersonBillingAccount } from '../../common/utils/stateUtils.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { useSessionData } from '../../public/site/SessionDataProvider.js';
import { openDialog } from '../../public/site/dialogStore.js';
import { processDeliveryDetails, upsertPersonBillingAccount } from '../../selfservice/actions/index.js';
import {
  getPunchoutDeviceChangeRequest,
  removePunchoutDeviceChangeRequest,
  setPunchoutDeviceChangeRequest,
} from '../../selfservice/common/localStorageUtils.js';
import { getUnattemptedOnlineModelCodes } from '../../selfservice/common/shopping-cart/shoppingCartFunctions.js';
import { cartContainsDirectDeliveryItems, isAnyCartItemBackordered } from '../DeviceCheckout/deviceCheckoutUtils.js';
import { DeviceCheckoutBillSummaryHeader } from '../DeviceCheckoutBillSummaryHeader/DeviceCheckoutBillSummaryHeader.js';
import { Loading } from '../Loading/index.js';
import { personalBillingAddressDetailsObjToAddress } from '../PersonalBillingDetails/PersonalBillingDetails.js';
import { PunchoutCheckoutConfirmation } from './partials/PunchoutCheckoutConfirmation.js';
import { PunchoutCheckoutDialog } from './partials/PunchoutCheckoutDialog.js';
import { ActiveStep, PunchoutCheckoutSteps } from './partials/PunchoutCheckoutSteps.js';
import type { PunchoutCheckoutLoaderData } from '../../common/loaders';
import type { DeviceChangeRequest } from '../../common/types/device.js';
import type { BillingAccount } from '../../generated/api/models.js';
import type { State } from '../../selfservice/common/store.js';
import type { DeviceCheckoutDeliveryDetailsType } from '../DeviceCheckoutDeliveryDetails/DeviceCheckoutDeliveryDetailsUtils.js';
import type { PersonalBillingDetailsData } from '../PersonalBillingDetails/PersonalBillingDetails.js';

import './PunchoutCheckout.scss';

export const PunchoutCheckout = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { revalidate } = useRevalidator();
  const [billingInfoConfirmed, setBillingInfoConfirmed] = useState(false);
  const { authenticatedUser, externalAuthErrors } = useAuth();
  const authenticationFailed = externalAuthErrors.some(
    error => error.type === CommonErrorType.BAD_REQUEST || error.type === CommonErrorType.FORBIDDEN
  );
  const isStronglyAuthenticated = authenticatedUser?.isStronglyAuthenticated;
  const [localBillingAccounts, setLocalBillingAccounts] = useState<BillingAccount[] | null>(null);
  const personBillingAccount = findPersonBillingAccount(localBillingAccounts ?? undefined);
  const deviceSubs = useSelector((state: State) => state.user?.authenticated?.subscriptions?.device, deepEqual) || [];
  const { cartItems, catalogs, catalogCode, deliveryDetails, selectedCatalogCode, deliveryMethods } = useSelector(
    (state: State) => ({
      catalogs: getPublishedCatalogs(state.selfservice?.virtualCatalogs?.items),
      selectedCatalogCode: state.selfservice?.virtualCatalogs?.selectedCatalogCode,
      cartItems: state.deviceCheckout?.cartItems || [],
      catalogCode: state.selfservice?.virtualCatalogs?.selectedCatalogCode,
      deliveryDetails: state.deviceCheckout?.deliveryDetails,
      deliveryMethods: state.deviceCheckout?.deliveryMethods?.lisOfDeliveryMethods,
    }),
    deepEqual
  );
  const { models: onlineModels } = useLoaderData() as PunchoutCheckoutLoaderData;
  const attemptedOnlineModelCodes = useRef(new Set<string>());
  useSyncCartPricesWithModels(onlineModels);
  useEffect(() => {
    fetchBillingAccounts({ useSearchService: true }, { isPunchout: true }).then(response => {
      setLocalBillingAccounts(response.billingAccounts || []);
    });
  }, []);
  const companyInfo = useSessionData().companyInfo;
  const corporateMsg = catalogs?.find(catalog => catalog.catalogCode === selectedCatalogCode)?.corporateMessage;
  useEffect(() => {
    if (corporateMsg && !authenticatedUser?.isStronglyAuthenticated) {
      openDialog({
        body: corporateMsg,
        header: t.WHAA(corporateMessagePopUpHeaderMsg),
        type: DialogType.GENERIC_INFO_DIALOG,
      });
    }
  }, [authenticatedUser?.isStronglyAuthenticated, corporateMsg]);

  const skipDeliveryDetails = authenticatedUser?.enabledFeatureFlags?.includes('skipDeliveryDetails') || false;
  const shipmentTypeSelectionEnabled =
    authenticatedUser?.enabledFeatureFlags?.includes('punchoutShipmentTypeSelection') || false;
  const hideDeviceChangeStep =
    authenticatedUser?.enabledFeatureFlags?.includes('hideDeviceChangeStep') || !deviceSubs.length || false;

  const deviceChangeRequest = getPunchoutDeviceChangeRequest();
  const [moveToCheckoutConfirmation, setMoveToCheckoutConfirmation] = useState(false);
  const [isBillingTermsAccepted, setBillingTermsAccepted] = useState<boolean>();
  const isOmalaiteLasku = hasEmployeePayables(cartItems || [], deviceChangeRequest);
  const shouldShowShipmentTypeSelection =
    shipmentTypeSelectionEnabled &&
    (cartContainsDirectDeliveryItems(cartItems, onlineModels) ||
      (cartItems.length > 1 && Boolean(isAnyCartItemBackordered(cartItems, onlineModels))));
  const showDeliveryDetailsStep = skipDeliveryDetails && shouldShowShipmentTypeSelection;

  const [deliveryDetailsObj, setDeliveryDetailsObj] = useState<DeviceCheckoutDeliveryDetailsType>(
    deliveryDetails ?? ({} as DeviceCheckoutDeliveryDetailsType)
  );
  const [isDeviceChangeStepDone, setDeviceChangeStepDone] = useState(false);

  const [activeStep, setActiveStep] = useState(
    hideDeviceChangeStep || (isOmalaiteLasku && authenticationFailed) ? ActiveStep.AUTH : ActiveStep.DEVICE_CHANGE
  );

  useEffect(() => {
    if (isOmalaiteLasku && authenticationFailed) {
      setActiveStep(ActiveStep.AUTH);
    }
  }, [authenticationFailed, isOmalaiteLasku]);

  const onDeviceChangeNextStep = (updatedDeviceChangeRequest: DeviceChangeRequest): void => {
    setPunchoutDeviceChangeRequest(updatedDeviceChangeRequest);
    setDeviceChangeStepDone(true);
  };
  useEffect(() => {
    if (deviceChangeRequest || hideDeviceChangeStep) {
      setDeviceChangeStepDone(true);
      if (
        (!isOmalaiteLasku && (isDeviceChangeStepDone || hideDeviceChangeStep)) ||
        (isOmalaiteLasku && billingInfoConfirmed && isDeviceChangeStepDone)
      ) {
        setActiveStep(ActiveStep.DELIVERY_DETAILS);
      } else {
        setActiveStep(isStronglyAuthenticated ? ActiveStep.BILLING : ActiveStep.AUTH);
      }
    }
  }, [
    billingInfoConfirmed,
    deviceChangeRequest,
    hideDeviceChangeStep,
    isDeviceChangeStepDone,
    isOmalaiteLasku,
    isStronglyAuthenticated,
  ]);

  useEffect(() => {
    if (
      billingInfoConfirmed &&
      skipDeliveryDetails &&
      !showDeliveryDetailsStep &&
      (isDeviceChangeStepDone || hideDeviceChangeStep)
    ) {
      setMoveToCheckoutConfirmation(true);
    }
  }, [
    billingInfoConfirmed,
    hideDeviceChangeStep,
    isDeviceChangeStepDone,
    showDeliveryDetailsStep,
    skipDeliveryDetails,
  ]);

  // The effect ensures online models are loaded before delivery calculation runs, and revalidates the loader
  // whenever the cart contains items not yet covered by it.
  // This fixes partial/total delivery detection on first load and keeps delivery details synchronized with state.
  useEffect(() => {
    const cartModelCodes = cartItems.map(item => item.onlineModelCode);
    const modelCodesToLoad = getUnattemptedOnlineModelCodes(
      cartModelCodes,
      onlineModels,
      attemptedOnlineModelCodes.current
    );
    modelCodesToLoad.forEach(code => attemptedOnlineModelCodes.current.add(code));
    if (modelCodesToLoad.length > 0) {
      revalidate();
    }
  }, [cartItems, onlineModels, revalidate]);

  useEffect(() => {
    if (deliveryMethods) {
      setDeliveryDetailsObj(getDeliveryDetailsObj(deliveryMethods, companyInfo, authenticatedUser, deliveryDetails));
    }
  }, [deliveryMethods, companyInfo, authenticatedUser, deliveryDetails, cartItems]);

  const onSaveDeliveryDetailsAndNext = (updatedDeliveryDetails: DeviceCheckoutDeliveryDetailsType) => {
    setDeliveryDetailsObj(updatedDeliveryDetails);
    dispatch(
      processDeliveryDetails(
        updatedDeliveryDetails,
        undefined,
        undefined,
        undefined,
        undefined,
        true,
        isOmalaiteLasku,
        true
      )
    );
    setMoveToCheckoutConfirmation(true);
  };

  const onSaveBillingDetailsAndNext = (values: PersonalBillingDetailsData) => {
    const moveToNextState = () => {
      setBillingInfoConfirmed(true);
    };

    if (!isBillingTermsAccepted) {
      // if terms not accepted, show error
      setBillingTermsAccepted(false);
      return;
    }

    const address = personalBillingAddressDetailsObjToAddress(values);
    const email = values.email;
    if (
      !personBillingAccount ||
      !deepEqual(address, personBillingAccount.payerAddress) ||
      email !== personBillingAccount.billReceiverEmail
    ) {
      // if person ba needs to be created or updated, then dispatch upsert action. move to next step after action is fulfilled
      dispatch(
        upsertPersonBillingAccount({ address, email }, personBillingAccount?.billingAccountId, undefined, () => {
          fetchBillingAccounts({ useSearchService: true }, { isPunchout: true }).then(response => {
            setLocalBillingAccounts(response.billingAccounts || []);
            moveToNextState();
          });
        })
      );
      return;
    }
    // if person ba exists, user clicks on edit but doesn't modify ba details, then move to next step
    moveToNextState();
  };

  const cartModelsLoaded = cartItems.every(item =>
    (onlineModels?.flatMap(model => model.onlineModelCode) || []).includes(item.onlineModelCode)
  );

  if (!cartItems || cartItems.length === 0 || !catalogCode || !cartModelsLoaded || !authenticatedUser) {
    return <Loading big />;
  }

  return (
    <div className="of-punchout-checkout">
      <div className="of-punchout-checkout--header">
        <DeviceCheckoutBillSummaryHeader
          commercialProducts={getConfiguredCommercialProducts(cartItems)}
          deliveryCharges={getDeliveryCharges(deliveryDetailsObj)}
          priceIncludesVAT={true}
          buttonText={t.PLAW('Return to the shop')}
          redirectToWebShopButton={() => {
            navigate(paths.PUNCHOUT_STORE);
            removePunchoutDeviceChangeRequest();
          }}
        />
      </div>
      <PunchoutCheckoutDialog />
      {!moveToCheckoutConfirmation ? (
        <PunchoutCheckoutSteps
          user={authenticatedUser}
          onSaveDeliveryDetailsAndNext={onSaveDeliveryDetailsAndNext}
          onSaveBillingDetailsAndNext={onSaveBillingDetailsAndNext}
          onSetBillingTerms={setBillingTermsAccepted}
          setDeliveryDetailsObj={setDeliveryDetailsObj}
          deliveryDetails={deliveryDetailsObj}
          billingInfoConfirmed={billingInfoConfirmed}
          isBillingTermsAccepted={isBillingTermsAccepted}
          isStronglyAuthenticated={isStronglyAuthenticated}
          isOmalaiteLasku={isOmalaiteLasku}
          hideDeviceChangeStep={hideDeviceChangeStep}
          skipDeliveryDetails={skipDeliveryDetails}
          shipmentTypeSelectionEnabled={shipmentTypeSelectionEnabled}
          showDeliveryDetailsStep={showDeliveryDetailsStep}
          skipDeliveryDetailsAndNext={() => setMoveToCheckoutConfirmation(true)}
          onDeviceChangeNextStep={onDeviceChangeNextStep}
          isDeviceChangeStepDone={isDeviceChangeStepDone}
          activeStep={activeStep}
          deviceSubs={deviceSubs}
        />
      ) : (
        <PunchoutCheckoutConfirmation
          cartItems={cartItems}
          catalogCode={catalogCode}
          deliveryDetails={deliveryDetailsObj}
          user={authenticatedUser}
          personBillingAccount={billingInfoConfirmed ? personBillingAccount : undefined}
          skipDeliveryDetails={skipDeliveryDetails}
          shipmentTypeSelectionEnabled={shipmentTypeSelectionEnabled}
          deviceChangeRequest={deviceChangeRequest}
        />
      )}
    </div>
  );
};
